import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createTenantUser } from "../../../../services/TenantsService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const AddUser = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const editUser = location.state?.user || null;
  const mode = location.state?.mode || "add";
  const isEditMode = mode === "edit" && !!editUser;
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    studentName: "",
    mobileNumber: "",
    email: "",
    password: "",
    landline: "",
  });

  // for all inputs
  useEffect(() => {
    if (isEditMode && editUser) {
      setFormData({
        studentName: editUser.name || "",
        mobileNumber: editUser.phone || "",
        email: editUser.email || "",
        password: "", // we usually do NOT show password
        landline: editUser.landLine || "",
      });
    }
  }, [isEditMode, editUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.studentName.trim())
      newErrors.studentName = "Name is required";
    if (!formData.mobileNumber)
      newErrors.mobileNumber = "Mobile Number is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (
      !/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(formData.email)
    ) {
      newErrors.email = "* Invalid email format";
    }
    if (!formData.password.trim() && mode !== "edit")
      newErrors.password = "Password is required";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    const payload = {
      name: formData.studentName,
      phone: formData.mobileNumber,
      email: formData.email,
      landLine: formData.landline,
      ...(isEditMode ? { id: editUser.id } : { password: formData.password }),
    };

    try {
      if (isEditMode) {
        const res = await createTenantUser(payload); // id in URL + in body
        if (res) {
          toast.success("User Updated successfully!", {
            position: "top-right",
            autoClose: 1000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        }
      } else {
        const res = await createTenantUser(payload);
        if (res) {
          toast.success("User created successfully!", {
            position: "top-right",
            autoClose: 1000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        }
      }

      navigate("/super-admin/all-users");
    } catch (error) {
      const message =
        error?.response?.data?.errorMessage ||
        error?.message ||
        "Something went wrong";
      Swal.fire("Error!", message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (isEditMode && editUser) {
      // Reset back to original edit data
      setFormData({
        studentName: editUser.name || "",
        mobileNumber: editUser.phone || "",
        email: editUser.email || "",
        password: "",
        landline: editUser.landLine || "",
      });
    } else {
      // Reset to empty for add mode
      setFormData({
        studentName: "",
        mobileNumber: "",
        email: "",
        password: "",
        landline: "",
      });
    }
  };

  return (
    <>
      <div className="page-titles d-flex justify-content-between align-items-center">
        <h4 className="mb-0">{isEditMode ? "Edit" : "Add"} Users</h4>
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item ">
            <Link to="/super-admin/all-users">user</Link>
          </li>
          <li className="breadcrumb-item active">
            <Link to={"#"}>{isEditMode ? "Edit User" : "Add User"}</Link>
          </li>
        </ol>
      </div>
      <div className="row">
        <div className="col-xl-12 col-xxl-12 col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">{isEditMode ? "Edit" : "Add"} User</h5>
            </div>
            <div className="card-body">
              <form autoComplete="off">
                {/* Decoy fields to prevent browser autofill */}
                <div
                  style={{
                    position: "absolute",
                    top: "-9999px",
                    left: "-9999px",
                    opacity: 0,
                    height: 0,
                    width: 0,
                    overflow: "hidden",
                  }}
                  aria-hidden="true"
                >
                  <input
                    type="text"
                    name="fake_username_autofill"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                  <input
                    type="password"
                    name="fake_password_autofill"
                    tabIndex={-1}
                    autoComplete="new-password"
                  />
                </div>

                <div className="row">
                  {/* Name of the Student */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="studentName">
                        Name<span className="text-danger">*</span>
                      </label>
                      <input
                        id="studentName"
                        name="studentName"
                        placeholder="Enter Full Name"
                        type="text"
                        className={`form-control ${
                          errors.studentName ? "is-invalid" : ""
                        }`}
                        value={formData.studentName}
                        onChange={handleChange}
                      />
                      {errors.studentName && (
                        <small className="text-danger m-lg-2">
                          {errors.studentName}
                        </small>
                      )}
                    </div>
                  </div>

                  {/* Mobile Number */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="mobileNumber">
                        Mobile Number<span className="text-danger">*</span>
                      </label>
                      <input
                        id="mobileNumber"
                        name="mobileNumber"
                        placeholder="Enter Mobile Number"
                        type="number"
                        className={`form-control ${
                          errors.mobileNumber ? "is-invalid" : ""
                        }`}
                        required
                        value={formData.mobileNumber}
                        onChange={handleChange}
                      />
                      {errors.mobileNumber && (
                        <small className="text-danger m-lg-2">
                          {errors.mobileNumber}
                        </small>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="email">
                        Email<span className="text-danger">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        placeholder="Enter Email"
                        type="email"
                        className={`form-control ${
                          errors.email ? "is-invalid" : ""
                        }`}
                        required
                        autoComplete="off"
                        value={formData.email}
                        onChange={handleChange}
                        readOnly={isEditMode}
                      />
                      {errors.email && (
                        <small className="text-danger m-lg-2">
                          {errors.email}
                        </small>
                      )}
                    </div>
                  </div>

                  {/* Password */}
                  {!isEditMode && (
                    <div className="col-sm-6">
                      <div className="form-group">
                        <label className="form-label" htmlFor="password">
                          Password
                        </label>

                        <div className="input-group pass-group">
                          <input
                            placeholder="Enter Password"
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            className="form-control pass-input"
                            style={
                              errors.password
                                ? {
                                    borderColor: "#dc3545",
                                    borderRight: "none",
                                  }
                                : {}
                            }
                            autoComplete="new-password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                          />

                          <span
                            className={`input-group-text pass-handle ${
                              showPassword ? "active" : ""
                            }`}
                            onClick={() => setShowPassword(!showPassword)}
                            style={
                              errors.password
                                ? {
                                    borderColor: "#dc3545",
                                    borderLeft: "none",
                                  }
                                : {}
                            }
                          >
                            <i className="fa fa-eye-slash" />
                            <i className="fa fa-eye" />
                          </span>
                        </div>
                        {errors.password && (
                          <small className="text-danger m-lg-2">
                            {errors.password}
                          </small>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Landline */}
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="landline">
                        Landline
                      </label>
                      <input
                        id="landline"
                        name="landline"
                        placeholder="Enter Landline Number"
                        type="text"
                        className="form-control"
                        value={formData.landline}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  {/* Submit & Cancel */}
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <button
                      className="btn btn-primary me-1"
                      disabled={loading}
                      onClick={handleSubmit}
                    >
                      {loading
                        ? isEditMode
                          ? "Updating..."
                          : "Submitting..."
                        : isEditMode
                          ? "Update"
                          : "Submit"}
                    </button>
                    <Link
                      to="/super-admin/all-users"
                      className="btn btn-danger light"
                    >
                      Cancel
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddUser;
